import { Controller, useFormContext, FieldError } from "react-hook-form";

import { useThemePreviewContext } from "containers/admin/clients/client/theme-page/contexts/theme-preview-context";

import { TextField } from "ui/text-field";
import { IconButton } from "ui/icon-button";

import { useAdobeFonts } from "containers/admin/clients/client/theme-page/preview/sub-tabs/fonts/adobe-fonts/use-adobe-fonts";
import { ADOBE_FONTS } from "containers/admin/clients/client/theme-page/preview/preview.constants";

import Icons from "assets";

export const AdobeFontInputController = () => {
  const { isEditMode } = useThemePreviewContext();
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const {
    isAdobeFontsRemovalPending,
    hasSubmitted,
    setHasSubmitted,
    handleSubmitAdobeFonts,
    handleClearAdobeFontsInput,
    handleUndoClearAdobeFontsInput,
    isAdobeFontsActionAvailable,
  } = useAdobeFonts();

  const getEndIcon = (showClearButton: boolean, value: string) => {
    const isErrOccured = !!(errors.fonts as Record<string, FieldError>)?.adobeFontsProjectId
      ?.message;

    const handleRemoveFont = () => {
      handleClearAdobeFontsInput(value?.toString() ?? "", isErrOccured);
    };

    return (
      <>
        {showClearButton && isEditMode && (
          <IconButton aria-label="Remove font" onClick={handleRemoveFont} variant="text">
            <Icons.Close />
          </IconButton>
        )}

        {isAdobeFontsRemovalPending && (
          <IconButton
            aria-label="Undo removing font"
            onClick={handleUndoClearAdobeFontsInput}
            variant="text">
            <Icons.Undo />
          </IconButton>
        )}
      </>
    );
  };

  return (
    <Controller
      name={ADOBE_FONTS.name}
      control={control}
      render={({ field, fieldState }) => {
        const errMessage = (errors.fonts as Record<string, FieldError>)?.adobeFontsProjectId
          ?.message;
        const userMaySaveFont = isAdobeFontsActionAvailable(field.value, !!fieldState.error);
        const showClearButton = !isAdobeFontsRemovalPending && (!!errMessage || field.value);
        const shouldDisableInput =
          isAdobeFontsRemovalPending ||
          (hasSubmitted && !userMaySaveFont && field.value && !fieldState.error);

        return (
          <div className="flex mb-3">
            <TextField
              inputSx={(theme) => ({
                ...(isAdobeFontsRemovalPending && {
                  textDecoration: "line-through",
                  color: theme.palette.grey[500],
                }),
                ...(shouldDisableInput && { pointerEvents: "none" }),
              })}
              endIcon={getEndIcon(showClearButton, field.value)}
              aria-invalid={!!errMessage}
              aria-describedby={errMessage && "adobe-fonts-error"}
              placeholder="Project ID..."
              disabled={!isEditMode || field.disabled}
              errMessage={errMessage}
              data-testid="adobe-font-project-id"
              label="Adobe Fonts Project ID"
              {...field}
              onChange={(e) => {
                if (shouldDisableInput) {
                  return;
                }
                field.onChange(e);
                setHasSubmitted(false);
              }}
              actionTrigger={
                <IconButton
                  aria-label="Save font"
                  size="large"
                  disabled={!userMaySaveFont || !isEditMode}
                  onClick={() => {
                    handleSubmitAdobeFonts(field.value);
                  }}>
                  <Icons.Checkmark />
                </IconButton>
              }
            />
          </div>
        );
      }}
    />
  );
};
